/**
 * Fase 17 — Log de Auditoria local (Dexie auditLog table).
 *
 * Captura automaticamente todos os writes das tabelas sincronizadas (commands,
 * deliveryOrders, kdsQueue) com timestamp + uid + nickname + resumo.
 *
 * Não sincroniza com Firebase (evita recursão) — fica local em cada dispositivo.
 * Para auditoria cross-device no futuro, o mesmo _writeUid/_writeDevice do
 * payload RTDB já permite reconstruir um log global.
 */
import db from '@/db';

const MAX_ENTRIES = 200; // ring buffer: mantém só os últimos 200 eventos

export const AUDIT_ACTIONS = {
  create: 'create',
  update: 'update',
  delete: 'delete',
};

/**
 * Gera um resumo humano da ação (ex: "Comanda #42 criada (R$ 25,00)").
 */
function humanize(table, action, record, modifications) {
  const num = record?.number != null ? `#${record.number}` : (record?.id ? `#${record.id}` : '');
  const label = {
    commands: 'Comanda',
    deliveryOrders: 'Pedido Delivery',
    kdsQueue: 'Item KDS',
  }[table] || table;

  if (action === 'create') {
    const total = record?.total != null ? ` (R$ ${Number(record.total).toFixed(2).replace('.', ',')})` : '';
    return `${label} ${num} criad${table === 'kdsQueue' ? 'o' : 'a'}${total}`;
  }
  if (action === 'delete') {
    return `${label} ${num} removid${table === 'kdsQueue' ? 'o' : 'a'}`;
  }
  // update — destaca o campo mais relevante (status, total, paymentMethod)
  if (modifications) {
    if (modifications.status) return `${label} ${num} → status=${modifications.status}`;
    if (modifications.paymentMethod) return `${label} ${num} fechada via ${modifications.paymentMethod}`;
    if (modifications.total != null) return `${label} ${num} total=R$ ${Number(modifications.total).toFixed(2).replace('.', ',')}`;
  }
  return `${label} ${num} atualizad${table === 'kdsQueue' ? 'o' : 'a'}`;
}

/**
 * Registra um evento de auditoria. Silencioso em falhas (não quebra o fluxo).
 * Se `overrideSummary` vier preenchido, usa ele em vez do resumo automático
 * (útil para ações específicas como "item removido da comanda").
 */
export async function logAudit({ table, action, record, modifications, deviceUid, deviceNickname, overrideSummary }) {
  try {
    const summary = overrideSummary || humanize(table, action, record, modifications);
    await db.auditLog.add({
      at: new Date().toISOString(),
      table,
      action,
      recordId: record?.id || null,
      recordNumber: record?.number || null,
      summary,
      deviceUid: deviceUid || null,
      deviceNickname: deviceNickname || null,
    });

    // Ring buffer — limita a MAX_ENTRIES, remove os mais antigos
    const count = await db.auditLog.count();
    if (count > MAX_ENTRIES) {
      const excess = count - MAX_ENTRIES;
      const oldestIds = await db.auditLog.orderBy('at').limit(excess).primaryKeys();
      await db.auditLog.bulkDelete(oldestIds);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[AUDIT] log fail (ignorado):', err?.message || err);
  }
}

/**
 * Últimos N eventos, ordem decrescente (mais recentes primeiro).
 */
export async function getRecentAudit(limit = 20) {
  const all = await db.auditLog.orderBy('at').reverse().limit(limit).toArray();
  return all;
}

export async function clearAuditLog() {
  await db.auditLog.clear();
}

/**
 * Formata "há 5 min atrás" em pt-BR.
 */
export function relativeTimeBR(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 45) return 'agora';
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d} dia${d > 1 ? 's' : ''}`;
}
